import React from 'react'
import { Snackbar, Button } from '@mui/material'

import MuiAlert from '@mui/material/Alert'

const NotificationsStateContext = React.createContext()
const NotificationsDispatchContext = React.createContext()

let notificationId = 0

function notificationReducer(state, action) {
  switch (action.type) {
    case 'ADD': {
      const customId = action.payload.options?.id
      // If custom ID provided and exists, update it
      if (customId) {
        const existingIndex = state.notifications.findIndex(
          (n) => n.id === customId
        )
        if (existingIndex >= 0) {
          const updated = [...state.notifications]
          updated[existingIndex] = {
            id: customId,
            severity: action.payload.severity,
            message: action.payload.message,
            options: action.payload.options || {},
          }
          return { ...state, notifications: updated }
        }
      }
      return {
        ...state,
        notifications: [
          ...state.notifications,
          {
            id: customId || ++notificationId,
            severity: action.payload.severity,
            message: action.payload.message,
            options: action.payload.options || {},
          },
        ],
      }
    }
    case 'REMOVE':
      return {
        ...state,
        notifications: state.notifications.filter(
          (n) => n.id !== action.payload.id
        ),
      }
    // Legacy support
    case 'OPEN': {
      const customId = action.payload.options?.id
      if (customId) {
        const existingIndex = state.notifications.findIndex(
          (n) => n.id === customId
        )
        if (existingIndex >= 0) {
          const updated = [...state.notifications]
          updated[existingIndex] = {
            id: customId,
            severity: action.payload.severity,
            message: action.payload.message,
            options: action.payload.options || {},
          }
          return { ...state, notifications: updated }
        }
      }
      return {
        ...state,
        notifications: [
          ...state.notifications,
          {
            id: customId || ++notificationId,
            severity: action.payload.severity,
            message: action.payload.message,
            options: action.payload.options || {},
          },
        ],
      }
    }
    case 'CLOSE':
      // Close the oldest notification
      return {
        ...state,
        notifications: state.notifications.slice(1),
      }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`)
    }
  }
}

function NotificationsProvider({ children }) {
  const [state, dispatch] = React.useReducer(notificationReducer, {
    notifications: [],
  })

  const showNotification = (severity, message, options) => {
    dispatch({ type: 'ADD', payload: { severity, message, options } })
  }

  const handleClose = (id, reason) => {
    const notification = state.notifications.find((n) => n.id === id)
    // Don't close on clickaway if persist is true
    if (notification?.options?.persist && reason === 'clickaway') {
      return
    }
    dispatch({ type: 'REMOVE', payload: { id } })
  }

  const handleActionClick = (notification) => {
    if (notification.options?.action?.onClick) {
      notification.options.action.onClick()
    }
    dispatch({ type: 'REMOVE', payload: { id: notification.id } })
  }

  return (
    <NotificationsStateContext.Provider value={{ state, showNotification }}>
      <NotificationsDispatchContext.Provider value={dispatch}>
        {state.notifications.map((notification, index) => (
          <Snackbar
            key={notification.id}
            open={true}
            autoHideDuration={notification.options?.persist ? null : 6000}
            onClose={(event, reason) => handleClose(notification.id, reason)}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            style={{ bottom: 24 + index * 70 }}
            ClickAwayListenerProps={{ mouseEvent: false, touchEvent: false }}
          >
            <MuiAlert
              elevation={6}
              variant="filled"
              onClose={() => handleClose(notification.id)}
              severity={notification.severity}
              action={
                notification.options?.action ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => handleActionClick(notification)}
                  >
                    {notification.options.action.label}
                  </Button>
                ) : undefined
              }
            >
              {notification.message}
            </MuiAlert>
          </Snackbar>
        ))}
        {children}
      </NotificationsDispatchContext.Provider>
    </NotificationsStateContext.Provider>
  )
}

function useNotificationsProvider() {
  const context = React.useContext(NotificationsStateContext)
  if (context === undefined) {
    throw new Error(
      'useNotificationsState must be used within a NotificationsProvider'
    )
  }
  return context
}

function useNotificationsDispatch() {
  const context = React.useContext(NotificationsDispatchContext)
  if (context === undefined) {
    throw new Error(
      'useNotificationsDispatch must be used within a NotificationsProvider'
    )
  }
  return context
}

export {
  NotificationsProvider,
  useNotificationsProvider,
  useNotificationsDispatch,
}
